<template>
  <ModalBaseNew
      :title="'Nastavení verze: ' + (settings?.version_tag || '')"
      :is-visible="isVisible"
      @update:is-visible="$emit('update:isVisible', $event)"
      modal-width="modal-lg"
  >
    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Načítám...</span>
      </div>
    </div>

    <div v-else-if="settings" class="container-fluid">
      <!-- Horní řada: Číslo verze a Tag -->
      <div class="row">
        <div class="col-2 form-group">
          <label class="form-label">Číslo verze</label>
          <input class="form-control" type="number" :value="settings.version" disabled>
        </div>
        <div class="col-4 form-group">
          <label class="form-label">Tag verze</label>
          <input class="form-control" type="text" :value="settings.version_tag" disabled>
        </div>
      </div>

      <!-- Popis verze -->
      <div class="row my-3">
        <div class="col-12 form-group">
          <label class="form-label">Popis verze</label>
          <input class="form-control" type="text" :value="settings.description" disabled>
        </div>
      </div>

      <!-- DPH, Přirážka a Sleva prořezu -->
      <div class="row my-3">
        <div class="col-3 form-group">
          <label class="form-label">DPH:</label>
          <select class="form-select" disabled>
            <option selected>{{ settings.vat }} %</option>
          </select>
        </div>
        <div class="col-4 form-group">
          <label class="form-label">Přirážka / sleva (%):</label>
          <input class="form-control" type="number" :value="settings.sale" disabled>
        </div>
        <div class="col-5 form-group">
          <label class="form-label">Sleva prořezu materiálu (Kč):</label>
          <input class="form-control" type="number" :value="settings.custom_cutoff_sale" disabled>
        </div>
      </div>

      <!-- Provize -->
      <div class="row my-3">
        <div class="col-3 form-group">
          <label class="form-label">Provize (%):</label>
          <input class="form-control" type="number" :value="settings.commission_rate || 0" disabled>
        </div>
      </div>

      <!-- Switche -->
      <div class="form-group my-3">
        <div class="form-check form-switch">
          <label class="form-check-label">Přepočítat cenu materiálu dle cenových skupin</label>
          <input class="form-check-input" type="checkbox" role="switch" :checked="settings.price_switch" disabled>
        </div>
      </div>
      <div class="form-group my-3">
        <div class="form-check form-switch">
          <label class="form-check-label">Nenabízej slevy na zbytkový materiál</label>
          <!-- V původním kódu je cutoff_sale_switch inverzní k checku (v-model="!cutoffSaleSwitch") -->
          <input class="form-check-input" type="checkbox" role="switch" :checked="!settings.cutoff_sale_switch"
                 disabled>
        </div>
      </div>

      <!-- Tabulka materiálů (ponechána dle původního zadání) -->
      <div class="col-12 mt-4">
        <h6>Rozměry materiálů a účtování</h6>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="table-light">
            <tr>
              <th>Název materiálu</th>
              <th>Rozměr (X x Y)</th>
              <th>Typ účtování</th>
              <th>Počet desek</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(dim, matId) in parsedDimensions" :key="matId">
              <td>{{ getMaterialName(matId) }}</td>
              <td>{{ dim.x }} x {{ dim.y }} mm</td>
              <td>{{ translateCutoff(dim.cutoff) }}</td>
              <td>{{ dim.count ? dim.count : '-' }}</td>
            </tr>
            <tr v-if="Object.keys(parsedDimensions).length === 0">
              <td colspan="3" class="text-center text-muted">Žádné specifické rozměry</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Vlastní footer -->
    <template #footer>
      <button type="button" class="btn btn-secondary" @click="$emit('update:isVisible', false)">Zavřít</button>
    </template>
  </ModalBaseNew>
</template>

<script setup>
import {ref, watch, computed, defineProps, defineEmits} from 'vue'
import ModalBaseNew from '@/components/base/ModalBaseNew.vue'
import myApi from "@/api/api"

const props = defineProps({
  isVisible: Boolean,
  versionId: {
    type: [Number, String],
    required: true
  }
})

defineEmits(['update:isVisible'])

const settings = ref(null)
const loading = ref(false)
const materialDetails = ref({})

// Překlady typů účtování
const cutoffTranslations = {
  'no_cutoff': 'nezapočítat',
  'base_cutoff': 'v nákupní ceně',
  'full_cutoff': 'v plné ceně'
}

const translateCutoff = (cutoff) => {
  return cutoffTranslations[cutoff] || cutoff
}

const getMaterialName = (matId) => {
  const item = materialDetails.value[matId]
  if (!item) return `ID: ${matId} (načítám...)`
  const materialOrder = item.order_only ? '- na objednávku' : '- skladem'
  return `${item.name}, ${item.surface || ''}, ${item.thickness?.name || ''}, ${item.material?.name || ''}, ${item.price_list?.name || ''} ${materialOrder}`
}

const parsedDimensions = computed(() => {
  if (!settings.value || !settings.value.dimensions) return {}
  if (typeof settings.value.dimensions === 'object') return settings.value.dimensions
  try {
    return JSON.parse(settings.value.dimensions)
  } catch (e) {
    console.error("Chyba parsování dimensions", e)
    return {}
  }
})

const fetchSettings = async () => {
  if (!props.versionId) return
  loading.value = true
  settings.value = null
  materialDetails.value = {}

  try {
    const data = await myApi.getCalculationVersionSettings(props.versionId)
    settings.value = data

    const dimensions = parsedDimensions.value
    const materialIds = Object.keys(dimensions)

    await Promise.all(materialIds.map(async (id) => {
      const item = await myApi.getPriceListItem(id)
      if (item) {
        materialDetails.value[id] = item
      }
    }))

  } catch (error) {
    console.error("Chyba při načítání nastavení verze:", error)
  } finally {
    loading.value = false
  }
}

watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    fetchSettings()
  }
})
</script>

<style scoped>
/* Zachování vizuální čistoty */
.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.table-sm {
  font-size: 0.85rem;
}
</style>